import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page, Button, Modal, Textinput, Flex, IconAsButton, theme } from '../../ui';
import { useIsIncarnated } from '../auth/hooks';
import { useMedia, useMediaActions } from './hooks';
import styled from 'styled-components';
import FoldersDirectory from './components/FoldersDirectory';
import FilesSection from './components/FilesSection';
import { CONTENT_WIDE } from '../../utils/media';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import FileDetails from './components/FileDetails';
import MediaBreadCrumbs from './components/MediaBreadcrumbs';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../ui/Page/PageHeader';
import PageControls from '../../ui/Page/PageControls';
import FileUploader from './uploaders/FileUploader';
import MediaTable from './components/MediaTable';
import PageSection from '../../ui/Page/PageSection';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  align-content: flex-start;
`;

const BreadcrumbsWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const MediaLibrary = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { folderId, fileId } = useParams();
  const isIncarnated = useIsIncarnated();
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [listMode, setListMode] = useState(true);

  const { mediaData, isLoading: mediaLoading, isError: mediaError } = useMedia({ directoryId: selectedFolder || '0', isIncarnated }, { skip: !selectedFolder });
  const { createDirectory, moveFile } = useMediaActions();

  const breadcrumbs = (mediaData?.breadcrumbs || []).slice().reverse();
  const files = mediaData?.files || [];
  const folders = files.filter((item) => item.type === 'dir');

  const filteredFiles = files.filter((item) => {
    if (item.type === 'dir') return false;
    if (selectedFilter !== 'all' && item.type !== selectedFilter) return false;
    if (query && !item.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const filteredFolders = folders.filter((folder) => !query || folder.name.toLowerCase().includes(query.toLowerCase()));

  const [modal, setModal] = useState(false);
  const [folderName, setFolderName] = useState('');

  useEffect(() => {
    setSelectedFolder(folderId || '0');
  }, [folderId]);

  const handleOpenFolder = (obj) => {
    setQuery('');
    navigate(`/media-library${obj?.id ? '/' + obj.id : ''}`);
  };

  const handleModal = useCallback(() => {
    setModal(!modal);
    setFolderName('');
  }, [setModal, modal]);

  const handleFolderName = useCallback(
    (e) => {
      setFolderName(e.target.value);
    },
    [setFolderName],
  );

  const createFolder = useCallback(async () => {
    await createDirectory({ directoryId: folderId || '0', name: folderName });
    setModal(false);
  }, [createDirectory, folderId, folderName, setModal]);

  const uploadFile = useCallback(
    async (file) => {
      await moveFile({ id: file.id, directoryId: selectedFolder });
    },
    [moveFile, selectedFolder],
  );

  const clearSearch = useCallback(() => {
    setQuery('');
  }, [setQuery]);

  const handleSearch = useCallback((value) => {
    setQuery(value);
  }, []);

  // MISSING TO DO:
  // improve performance
  // preview of "other" files in modal details - pdf, doc, etc

  return (
    <Page
      noScroll={true}
      size={CONTENT_WIDE}
      gap='30'
      staticElements={
        <PageHeader>
          <PageControls
            onClearSearch={clearSearch}
            onSearch={handleSearch}
            placeholder={t('media.searchPlaceholder')}
            openModelButton={
              <Flex gap='10'>
                <FileUploader id={'mediaUploadfile'} saveAction={uploadFile} label={t('media.uploadFiles')} noMediaLibrary />
                <Button $variant='customized' $bgcolor={theme.colors.PRIMARY_LIGHT} icon='CreateFolder' onClick={handleModal}>
                  {t('media.createFolders')}
                </Button>
              </Flex>
            }
          >
            <BreadcrumbsWrapper>
              <MediaBreadCrumbs onOpenFolder={handleOpenFolder} breadcrumbs={breadcrumbs} />
              <div>
                <IconAsButton
                  iconName='Menu'
                  color={listMode ? theme.colors.PRIMARY : null}
                  variant='action'
                  tooltipLabel={t('media.list')}
                  clickHandle={() => setListMode(true)}
                />
                <IconAsButton
                  iconName='ViewModule'
                  color={!listMode ? theme.colors.PRIMARY : null}
                  variant='action'
                  tooltipLabel={t('media.grid')}
                  clickHandle={() => setListMode(false)}
                />
              </div>
            </BreadcrumbsWrapper>
          </PageControls>
        </PageHeader>
      }
    >
      <DndProvider backend={HTML5Backend}>
        <PageSection size={CONTENT_WIDE}>
          <Container>
            {listMode ? (
              <MediaTable
                selectedFolder={selectedFolder}
                folders={filteredFolders}
                mediaFiles={filteredFiles}
                openFolder={handleOpenFolder}
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
                breadcrumbs={breadcrumbs}
                isLoading={mediaLoading}
                isError={mediaError}
              />
            ) : (
              <>
                <FoldersDirectory
                  selectedFolder={selectedFolder}
                  data={filteredFolders}
                  openFolder={handleOpenFolder}
                  withDND={true}
                  directoryId={selectedFolder}
                />
                <FilesSection
                  withDND={true}
                  currentDirectory={selectedFolder}
                  data={filteredFiles}
                  selectedFilter={selectedFilter}
                  setSelectedFilter={setSelectedFilter}
                  isLoading={mediaLoading}
                  isError={mediaError}
                />
              </>
            )}
          </Container>
        </PageSection>
      </DndProvider>
      <FileDetails fileId={fileId} />
      <Modal
        open={modal}
        onClose={handleModal}
        title={t('media.newFolder')}
        acceptLabel={t('globals.create')}
        onAccept={createFolder}
        onCancel={handleModal}
        showActions
      >
        <Textinput label={t('globals.name')} propName={'Name'} defaultValue={folderName} onBlurValue={handleFolderName} />
      </Modal>
    </Page>
  );
};

export default MediaLibrary;
